import useFirebase from '@hooks/firebase.hook';
import { SetStateAction, useEffect, useState } from 'react';
import type {
	Cities,
	HandleAutocompleteSearch,
	HandleSearchPressed,
	SearchHook,
} from '../interfaces/search.interfaces';
import type { ClienteSuite, DivisionCliente } from '@interfaces/firestore-documents.interfaces';
import useRequests from '@hooks/requests.hook';
import { CityResponseData, SifaDiv, SifaNIT } from '@interfaces/requests.interfaces';
import { MAX_SEARCH_LENGTH, MIN_SEARCH_LENGTH } from '../constants/search.constants';
import SearchFilters from '../constants/search-filters.enum';
import { parse as queryString } from 'query-string';
import { formatName, verifyAccents } from '@utils/texts';
import { useEveryNSeconds } from '@hooks/timer.hook';
import { FirebaseProjects } from '@interfaces/firebase.interfaces';
import { getDocs } from 'firebase/firestore';

function useSearch(): SearchHook {
	// States
	const [autocompleteData, setAutocompleteData] = useState<ClienteSuite[]>([]);
	const [autocompleteLoading, setAutocompleteLoading] = useState<boolean>(false);
	const [autocompleteText, setAutocompleteText] = useState<string>('');
	const [autocompleteTimeout, setAutocompleteTimeout] = useState<NodeJS.Timeout | null>(null);
	const [needAutocomplete, setNeedAutocomplete] = useState<boolean>(true);
	const [cities, setCities] = useState<Cities[]>([]);
	const [data, setData] = useState<ClienteSuite | null>(null);
	const [filter, setFilter] = useState<SearchFilters>(SearchFilters.Identification);
	const [divisionNumber, setDivisionNumber] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [pressed, setPressed] = useState<boolean>(false);
	const [text, setText] = useState<string>('');
	const [valueEmail, setValueEmail] = useState('');
	const [cityData, setCityData] = useState<CityResponseData | null>(null);
	const [sifaNIT, setSifaNIT] = useState<SifaNIT | null>(null);
	const [sifaDIV, setSifaDIV] = useState<SifaDiv[]>([]);
	const [chosenIdentification, setChosenIdentification] = useState<string>('');
	// Hooks
	const { accounts, terminals, handleQuery } = useFirebase();
	const { requestCity, requestAdministraCliente, requestBackendSuite } = useRequests();
	// Private functions
	async function updateData(data: ClienteSuite): Promise<ClienteSuite> {
		// Obtenemos la información de SIFA.

		data.divisiones = data.divisiones.map((div) => ({
			...div,
			nombre_div: formatName(div.nombre_div),
			procesos: div.procesos.map((process) => ({
				...process,
				id_proceso_text: process.id_proceso.toString().padStart(6, '0'),
				nombre_proceso: formatName(process.nombre_proceso),
			})),
		}));

		data.nombre = formatName(data.nombre);
		// data.estado = tSifaNIT ? (tSifaNIT.estado ? 'Activo' : 'Inactivo') : 'Sin acuerdo';

		return data;
	}

	const queryClienteById = async (identificacionMatch: string): Promise<ClienteSuite[]> => {
		try {
			const response = (await requestAdministraCliente(
				`clientes/busqueda/id?identificacionMatch=${identificacionMatch}`,
				'GET'
			)) as any;
			if (response.isError) {
				throw new Error(response.message || response.data.message);
			}
			return Promise.resolve(response.data);
		} catch (error: any) {
			return Promise.resolve([]);
		}
	};

	// Functions
	function reset(): void {
		setAutocompleteData([]);
		setAutocompleteLoading(false);
		setAutocompleteText('');

		if (autocompleteTimeout) {
			clearTimeout(autocompleteTimeout);
			setAutocompleteTimeout(null);
		}

		setNeedAutocomplete(true);

		setData(null);
		setFilter(SearchFilters.Identification);
		setDivisionNumber('');
		setLoading(false);
		setPressed(false);
		setText('');

		setCityData(null);
		setSifaNIT(null);
		setSifaDIV([]);
	}

	function verifyText(str: string, toCompare: SearchFilters = filter): boolean {
		if (toCompare === SearchFilters.Identification) {
			return new RegExp('^[a-zA-Z0-9 -]+$').test(str) && !verifyAccents(str);
		}

		return true;
	}

	// Handlers
	const handleAutocompleteSearch: HandleAutocompleteSearch = (value) => {
		setAutocompleteText(value);

		if (!needAutocomplete) return;

		if (
			(value.length < MIN_SEARCH_LENGTH || value.length > MAX_SEARCH_LENGTH) &&
			filter === SearchFilters.Identification
		) {
			return;
		}

		setAutocompleteLoading(true);

		if (autocompleteTimeout) {
			clearTimeout(autocompleteTimeout);
			setAutocompleteTimeout(null);
		}

		setAutocompleteTimeout(
			setTimeout(async function () {
				setAutocompleteTimeout(null);

				const endValue = value.replace(/.$/, (c) => String.fromCharCode(c.charCodeAt(0)));

				const clientes = await queryClienteById(endValue);

				const newData: ClienteSuite[] = clientes.map((doc: { identificacion: any }) => {
					return {
						identificacion: doc.identificacion,
						codigo_departamento: 0,
						codigo_municipio: 0,
						direccion: '',
						divisiones: [],
						estado: 'Activo',
						nombre: '',
						telefono_celular: '',
						telefono_fijo: '',
						tipo_identificacion: {
							codigo: 0,
							nombre: '',
						},
						direccion_rango: '',
						rangos_cliente: [],
					};
				});

				setAutocompleteData(newData);
				setAutocompleteLoading(false);
			}, 500)
		);
	};
	function handleChangeFilter(e: { target: { value: SetStateAction<SearchFilters> } }) {
		setAutocompleteText('');
		setValueEmail('');
		setFilter(e.target.value);
	}
	const handleSearchPressed: HandleSearchPressed = async (data, toSearch, toCompare = filter) => {
		const searchedData = autocompleteData;
		const searchedText = data?.identificacion ?? toSearch ?? autocompleteText;

		let clientData = data ?? null;

		// Limpiamos los datos del autocompletado.
		setAutocompleteData([]);
		setAutocompleteLoading(false);
		setNeedAutocomplete(true);
		setAutocompleteText('');

		if (autocompleteTimeout) {
			clearTimeout(autocompleteTimeout);
			setAutocompleteTimeout(null);
		}

		// Iniciamos el loader del component.
		setData(null);
		setFilter(toCompare);
		setLoading(true);
		setPressed(true);
		setText(searchedText);

		setCityData(null);
		setSifaNIT(null);
		setSifaDIV([]);

		// Colocamos la búsqueda en la query de la URL.
		const { search: searchQuery, filter: filterQuery } = queryString(location.search);

		if (
			searchQuery != searchedText ||
			filterQuery != toCompare ||
			location.search.length === 0
		) {
			if (location.pathname === '/admin/customers') {
				window.history.pushState(
					null,
					'',
					`${location.pathname}?search=${searchedText}&filter=${toCompare}`
				);
			} else {
				window.location.href = `/admin/customers?search=${searchedText}&filter=${toCompare}`;
			}
		}

		// Verificamos si el autocompletado fue ejecutado con anterioridad y obtenemos la información del cliente
		// desde ahí, sino lo consultará en Firestore.
		if (searchedData.length > 0 && !clientData && toCompare === 'identificacion') {
			clientData = searchedData.find((c) => c[toCompare] === searchedText) ?? null;
		}

		if (!clientData) {
			const dataCliente: any = await requestAdministraCliente(`clientes/${searchedText}`);
			if (dataCliente.data) {
				const dataRangosCliente: any = await requestAdministraCliente(
					`rangos/clientes/${searchedText}`
				);
				clientData = {
					identificacion: searchedText,
					codigo_departamento: dataCliente.data.codigo_departamento,
					codigo_municipio: dataCliente.data.codigo_municipio,
					direccion: dataCliente.data.direccion,
					divisiones: [],
					estado: 'Activo',
					nombre: dataCliente.data.nombre,
					telefono_celular: dataCliente.data.telefono_celular,
					telefono_fijo: dataCliente.data.telefono_fijo,
					tipo_identificacion: dataCliente.data.tipo_identificacion,
					direccion_rango: dataCliente.data.direccion,
					rangos_cliente: dataRangosCliente.data,
				};
			}
		}

		if (clientData) {
			setData(await updateData(clientData));
		}

		setLoading(false);
	};
	const getData = async () => {
		setLoading(true);
		const dataCliente: any = await requestAdministraCliente(`clientes/${data?.identificacion}`);
		const dataDivisiones: any = await requestAdministraCliente(
			`divisiones/clientes/${data?.identificacion}`
		);

		if (dataDivisiones && dataCliente) {
			const divisiones: DivisionCliente[] = [];
			for (const division of dataDivisiones.data) {
				const dataProcesos: any = await requestAdministraCliente(
					`procesos/divisiones?identificacion=${data?.identificacion}&division=${division.codigo_div}`
				);
				const habilitadoRetenerGuia: any = await requestAdministraCliente(
					`divisiones/estado?id_cliente=${data?.identificacion}&divisiones=${division.codigo_div}`
				);
				divisiones.push({
					...division,
					agw_ranges: division.agw_ranges
						.sort(
							(a: { en_uso: any }, b: { en_uso: any }) =>
								Number(b.en_uso) - Number(a.en_uso)
						)
						.sort(
							(a: { terminado: any }, b: { terminado: any }) =>
								Number(a.terminado) - Number(b.terminado)
						),
					procesos: dataProcesos.data.map((process: any) => {
						return {
							...process,
							ranges: process.ranges
								.sort(
									(a: { en_uso: any }, b: { en_uso: any }) =>
										Number(b.en_uso) - Number(a.en_uso)
								)
								.sort(
									(a: { terminado: any }, b: { terminado: any }) =>
										Number(a.terminado) - Number(b.terminado)
								),
						};
					}),
					habilitado_retencion_guia: habilitadoRetenerGuia.response.data.habilitado,
				});
			}
			const dataRangosCliente: any = await requestAdministraCliente(
				`rangos/clientes/${data?.identificacion}`
			);
			let dataSifa: ClienteSuite = {
				identificacion: data?.identificacion || '',
				codigo_departamento: dataCliente.data.codigo_departamento,
				codigo_municipio: dataCliente.data.codigo_municipio,
				direccion: dataCliente.data.direccion,
				divisiones,
				estado: 'Activo',
				nombre: dataCliente.data.nombre,
				telefono_celular: dataCliente.data.telefono_celular,
				telefono_fijo: dataCliente.data.telefono_fijo,
				tipo_identificacion: dataCliente.data.tipo_identificacion,
				direccion_rango: dataCliente.data.direccion,
				rangos_cliente: dataRangosCliente.data,
			};
			let tSifaDIV = sifaDIV,
				tSifaNIT = sifaNIT;

			try {
				if (tSifaDIV.length === 0 || tSifaDIV[0].dni !== data?.identificacion) {
					const divResponse = await requestBackendSuite<SifaDiv[]>(
						`sifa/divisiones-cliente/${data?.identificacion}`
					);

					if (divResponse.isError) {
						throw new Error(divResponse.message);
					}

					tSifaDIV = divResponse.data;
					setSifaDIV(divResponse.data);
				}

				if (!tSifaNIT || tSifaNIT.dni !== data?.identificacion) {
					const nitResponse = (await requestBackendSuite<SifaNIT>(
						`sifa/clientes/${data?.identificacion}`
					)) as any;

					tSifaNIT = nitResponse.data || '';
					setSifaNIT(nitResponse.data || '');
				}
			} catch (error) {
				// 	console.error('Consultas sifa', error);
			}

			// Si logramos obtener el código DANE desde SIFA entonces obtenemos el nombre de la ciudad y
			// la añadimos a la dirección del cliente.
			if (tSifaNIT) {
				let tCityData = cityData;
				if (!tCityData || tCityData.codigo !== tSifaNIT.codigoDane) {
					try {
						const cityResponse = await requestCity(tSifaNIT.codigoDane);
						tCityData = cityResponse;
						setCityData(cityResponse);
					} catch (error) {
						console.error('Request city name', error);
					}
				}

				if (tCityData) {
					dataSifa = {
						...dataSifa,
						area_telefono: tCityData.area_telefono,
						direccion: dataSifa?.direccion.includes(tCityData.nombre)
							? dataSifa.direccion
							: `${dataSifa?.direccion}, ${tCityData.nombre}`,
						direccion_rango: dataSifa.direccion,
					};
				}
			}

			if (tSifaDIV.length > 0) {
				const mapData: { [key: string]: SifaDiv } = tSifaDIV.reduce(
					(acc: { [key: string]: SifaDiv }, cur) => {
						if (!acc[cur.divisionComercial]) {
							acc[cur.divisionComercial] = cur;
						}

						return acc;
					},
					{}
				);

				dataSifa.divisiones = dataSifa.divisiones.map((div) => {
					const divData = mapData[div.codigo_div];

					return {
						...div,
						// activo: divData == undefined ? false : div.activo,
						nombre_asesor_comercial: formatName(divData?.nombreAsesor ?? ''),
						terminal_de_cobro: divData
							? terminals[divData.codigoTerminalCobro] ?? ''
							: '',
						terminal_de_cuenta: divData
							? terminals[divData.codigoTerminalCuenta] ?? ''
							: '',
						tipo_de_cuenta: divData ? accounts[divData.tipoDeCuenta] ?? '' : '',
					};
				});
			}
			dataSifa.estado = tSifaNIT ? (tSifaNIT.estado ? 'Activo' : 'Inactivo') : 'Sin acuerdo';
			setChosenIdentification(dataSifa.identificacion);
			setData(dataSifa);
		}
		setLoading(false);
	};
	const getDataById = async (identificacion: string) => {
		const dataCliente: any = await requestAdministraCliente(`clientes/${identificacion}`);
		const dataDivisiones: any = await requestAdministraCliente(
			`divisiones/clientes/${identificacion}`
		);
		if (dataDivisiones && dataCliente) {
			const divisiones: DivisionCliente[] = [];
			for (const division of dataDivisiones.data) {
				const dataProcesos: any = await requestAdministraCliente(
					`procesos/divisiones?identificacion=${identificacion}&division=${division.codigo_div}`
				);
				divisiones.push({
					...division,
					agw_ranges: division.agw_ranges
						.sort(
							(a: { en_uso: any }, b: { en_uso: any }) =>
								Number(b.en_uso) - Number(a.en_uso)
						)
						.sort(
							(a: { terminado: any }, b: { terminado: any }) =>
								Number(a.terminado) - Number(b.terminado)
						),
					procesos: dataProcesos.data.map((process: any) => {
						return {
							...process,
							ranges: process.ranges
								.sort(
									(a: { en_uso: any }, b: { en_uso: any }) =>
										Number(b.en_uso) - Number(a.en_uso)
								)
								.sort(
									(a: { terminado: any }, b: { terminado: any }) =>
										Number(a.terminado) - Number(b.terminado)
								),
						};
					}),
				});
			}
			const dataRangosCliente: any = await requestAdministraCliente(
				`rangos/clientes/${identificacion}`
			);
			let dataSifa: ClienteSuite = {
				identificacion: identificacion || '',
				codigo_departamento: dataCliente.data.codigo_departamento,
				codigo_municipio: dataCliente.data.codigo_municipio,
				direccion: dataCliente.data.direccion,
				divisiones,
				estado: 'Activo',
				nombre: dataCliente.data.nombre,
				telefono_celular: dataCliente.data.telefono_celular,
				telefono_fijo: dataCliente.data.telefono_fijo,
				tipo_identificacion: dataCliente.data.tipo_identificacion,
				direccion_rango: dataCliente.data.direccion,
				rangos_cliente: dataRangosCliente.data,
			};
			let tSifaDIV = sifaDIV,
				tSifaNIT = sifaNIT;

			try {
				if (tSifaDIV.length === 0 || tSifaDIV[0].dni !== identificacion) {
					const divResponse = await requestBackendSuite<SifaDiv[]>(
						`sifa/divisiones-cliente/${identificacion}`
					);

					if (divResponse.isError) {
						throw new Error(divResponse.message);
					}

					tSifaDIV = divResponse.data;
					setSifaDIV(divResponse.data);
				}

				if (!tSifaNIT || tSifaNIT.dni !== identificacion) {
					const nitResponse = (await requestBackendSuite<SifaNIT>(
						`sifa/clientes/${identificacion}`
					)) as any;

					tSifaNIT = nitResponse.data || '';
					setSifaNIT(nitResponse.data);
				}
			} catch (error) {
				// 	console.error('Consultas sifa', error);
			}

			// Si logramos obtener el código DANE desde SIFA entonces obtenemos el nombre de la ciudad y
			// la añadimos a la dirección del cliente.
			if (tSifaNIT) {
				let tCityData = cityData;
				if (!tCityData || tCityData.codigo !== tSifaNIT.codigoDane) {
					try {
						const cityResponse = await requestCity(tSifaNIT.codigoDane);
						tCityData = cityResponse;
						setCityData(cityResponse);
					} catch (error) {
						console.error('Request city name', error);
					}
				}

				if (tCityData) {
					dataSifa = {
						...dataSifa,
						area_telefono: tCityData.area_telefono,
						direccion: dataSifa?.direccion.includes(tCityData.nombre)
							? dataSifa.direccion
							: `${dataSifa?.direccion}, ${tCityData.nombre}`,
						direccion_rango: dataSifa.direccion,
					};
				}
			}

			if (tSifaDIV.length > 0) {
				const mapData: { [key: string]: SifaDiv } = tSifaDIV.reduce(
					(acc: { [key: string]: SifaDiv }, cur) => {
						if (!acc[cur.divisionComercial]) {
							acc[cur.divisionComercial] = cur;
						}

						return acc;
					},
					{}
				);

				dataSifa.divisiones = dataSifa.divisiones.map((div) => {
					const divData = mapData[div.codigo_div];

					return {
						...div,
						// activo: divData == undefined ? false : div.activo,
						nombre_asesor_comercial: formatName(divData?.nombreAsesor ?? ''),
						terminal_de_cobro: divData
							? terminals[divData.codigoTerminalCobro] ?? ''
							: '',
						terminal_de_cuenta: divData
							? terminals[divData.codigoTerminalCuenta] ?? ''
							: '',
						tipo_de_cuenta: divData ? accounts[divData.tipoDeCuenta] ?? '' : '',
					};
				});
			}
			dataSifa.estado = tSifaNIT ? (tSifaNIT.estado ? 'Activo' : 'Inactivo') : 'Sin acuerdo';
			setChosenIdentification(dataSifa.identificacion);

			setData(dataSifa);
		}
	};
	const getCities = async () => {
		const citiesQuery = handleQuery(FirebaseProjects.MANTENIMIENTO_OP, 'Ciudades');
		if (citiesQuery) {
			const result = await getDocs(citiesQuery);
			const arrCities: Cities[] = [];
			result.forEach((result) => {
				if (result.data().activo) {
					arrCities.push(result.data() as Cities);
				}
			});
			setCities(arrCities);
		}
	};
	// Effects
	useEffect(() => {
		getCities();
		if (data?.identificacion) {
			getData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data?.identificacion]);

	useEveryNSeconds(() => {
		if (chosenIdentification) {
			getDataById(chosenIdentification);
		}
	});

	return {
		// States
		autocompleteData,
		autocompleteLoading,
		autocompleteText,
		needAutocomplete,
		setNeedAutocomplete,
		valueEmail,
		setValueEmail,
		data,
		filter,
		divisionNumber,
		setDivisionNumber,
		loading,
		pressed,
		text,

		// Functions
		reset,
		verifyText,
		cities,
		getDataById,
		// Handlers
		handleAutocompleteSearch,
		handleSearchPressed,
		handleChangeFilter,
	};
}

export default useSearch;
